/**
 * Minimal IPv4 / CIDR helpers used by the Apprise VM validation (pure, no deps).
 */

export interface ParsedCidr {
  /** Network address as an unsigned 32-bit integer (host bits as given). */
  address: number
  prefix: number
  /** First address of the block (host bits cleared). */
  start: number
  /** Last address of the block. */
  end: number
}

const IPV4_RE = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/

export function parseIpv4(value: string): number | null {
  const m = IPV4_RE.exec(value.trim())
  if (!m) return null
  const octets = m.slice(1).map(Number)
  if (octets.some((o, i) => o > 255 || (m[i + 1].length > 1 && m[i + 1].startsWith('0')))) return null
  return ((octets[0] << 24) >>> 0) + (octets[1] << 16) + (octets[2] << 8) + octets[3]
}

export function parseCidr(value: string): ParsedCidr | null {
  const parts = value.trim().split('/')
  if (parts.length !== 2 || !/^\d{1,2}$/.test(parts[1])) return null
  const address = parseIpv4(parts[0])
  const prefix = Number(parts[1])
  if (address === null || prefix < 0 || prefix > 32) return null
  const size = 2 ** (32 - prefix)
  const start = address - (address % size)
  return { address, prefix, start, end: start + size - 1 }
}

/** True when the address has no host bits set (Azure rejects e.g. 10.60.1.0/16). */
export function isAlignedCidr(c: ParsedCidr): boolean {
  return c.address === c.start
}

export function cidrContains(outer: ParsedCidr, inner: ParsedCidr): boolean {
  return inner.start >= outer.start && inner.end <= outer.end
}

export function cidrOverlaps(a: ParsedCidr, b: ParsedCidr): boolean {
  return a.start <= b.end && b.start <= a.end
}

export function ipInCidr(ip: number, c: ParsedCidr): boolean {
  return ip >= c.start && ip <= c.end
}
